import { useFileRecordDownload } from "@/hooks/useFileRecordDownload";
import { attachFileRecordDownloadVariants } from "@/object-actions/attachFileRecordDownloadVariants";
import {
  attachTextNoteDownloadVariants,
  type TextNoteDownloadParams,
} from "@/object-actions/attachTextNoteDownloadVariants";
import {
  type FileRecordActionHandlerDeps,
  useFileRecordObjectActionHandlers,
} from "@/object-actions/handlers/fileRecord";
import { createRemoteFileRecordExportRunner } from "@/object-actions/handlers/fileRecordDownload";
import { useObjectActions } from "@/object-actions/renderers/useObjectActions";
import { useAttachAddToWorkspaceSubmenu } from "@/object-actions/transfer/useAddToWorkspaceSubmenu";
import type {
  ActionScope,
  ActionSurface,
  ObjectActionHandlers,
  ObjectKind,
  ResolvedObjectAction,
} from "@/object-actions/types";
import {
  fileRecordEverPublished,
  resolveFileRecordShowPublish,
} from "@/panes/file-record-detail/publish";
import type { FileRecord } from "@cohesive/api-client";
import { useMemo } from "react";

export interface UseCanvasEntityObjectActionsParams {
  kind: ObjectKind | null;
  surface: ActionSurface;
  enabled?: boolean;
  scope?: ActionScope;
  nodeHandlers: ObjectActionHandlers;
  fileRecord?: FileRecord | null;
  fileRecordHandlerDeps?: FileRecordActionHandlerDeps;
  /** Expands the text note's `download` action into its format submenu. */
  textNoteDownload?: TextNoteDownloadParams | null;
  state?: {
    canEdit?: boolean;
    canShare?: boolean;
    canDelete?: boolean;
    copyDisabled?: boolean;
    zOrderDisabled?: boolean;
    hasWebLink?: boolean;
    hasDetailView?: boolean;
    showPublish?: boolean;
    isPublished?: boolean;
    /** The node is an HTML block — adds Expand to a document's actions.
     *  Forwarded with the rest of `state`. */
    isCanvasIntrinsic?: boolean;
    /** The node's record is gone — the card renders Unavailable. */
    fileRecordUnavailable?: boolean;
  };
}

export function useCanvasEntityObjectActions({
  kind,
  surface,
  enabled = true,
  scope = "canonical",
  nodeHandlers,
  fileRecord,
  fileRecordHandlerDeps = {},
  textNoteDownload = null,
  state = {},
}: UseCanvasEntityObjectActionsParams): {
  actions: ResolvedObjectAction[];
  kind: ObjectKind | null;
} {
  const isFileRecordEntity =
    kind?.type === "canvas-node" &&
    kind.entityBase === "file-record" &&
    !!fileRecord;

  const fileRecordHandlers = useFileRecordObjectActionHandlers(
    fileRecord ?? ({} as FileRecord),
    fileRecordHandlerDeps,
  );

  const handlers = useMemo(() => {
    if (!isFileRecordEntity) {
      return nodeHandlers;
    }

    const merged = { ...nodeHandlers };
    for (const [id, handler] of Object.entries(fileRecordHandlers)) {
      if (handler !== undefined) {
        merged[id as keyof ObjectActionHandlers] = handler;
      }
    }
    return merged;
  }, [fileRecordHandlers, isFileRecordEntity, nodeHandlers]);

  const { actions: resolvedActions } = useObjectActions({
    kind: kind ?? { type: "canvas-node", nodeType: "entity" },
    surface,
    scope,
    enabled: enabled && !!kind,
    state: {
      onCanvasDetail: isFileRecordEntity,
      canShare:
        state.canShare ??
        (fileRecord ? fileRecord.can_edit !== false : undefined),
      showPublish:
        state.showPublish ??
        (fileRecord
          ? resolveFileRecordShowPublish({
              record: fileRecord,
              canEdit: state.canEdit ?? fileRecord.can_edit !== false,
              scope,
            })
          : false),
      isPublished:
        state.isPublished ??
        (!!fileRecord && fileRecordEverPublished(fileRecord)),
      ...state,
    },
    handlers,
  });

  const { download } = useFileRecordDownload();
  const remoteExport = useMemo(
    () =>
      fileRecord
        ? createRemoteFileRecordExportRunner(fileRecord, download)
        : null,
    [download, fileRecord],
  );

  const baseActions = useMemo(() => {
    if (
      kind?.type === "canvas-node" &&
      kind.nodeType === "text-note" &&
      textNoteDownload
    ) {
      return attachTextNoteDownloadVariants(resolvedActions, textNoteDownload);
    }
    if (!isFileRecordEntity || !fileRecord) {
      return resolvedActions;
    }

    return attachFileRecordDownloadVariants(resolvedActions, {
      record: fileRecord,
      runExport: remoteExport?.runExport ?? (async () => {}),
      isVariantDisabled: remoteExport?.isVariantDisabled,
    });
  }, [
    fileRecord,
    isFileRecordEntity,
    kind,
    remoteExport,
    resolvedActions,
    textNoteDownload,
  ]);

  // Add to workspace opens the home-workspace-locked destination cascade on
  // the node's menus, same as every other file surface. The node's live box
  // rides along so a promoted block remembers the size the user last saw.
  const actions = useAttachAddToWorkspaceSubmenu(
    baseActions,
    isFileRecordEntity ? (fileRecord ?? null) : null,
    isFileRecordEntity && fileRecord
      ? fileRecordHandlerDeps.resolveBlockDisplaySize?.(fileRecord)
      : undefined,
  );

  return { actions, kind };
}
